/**
 * «Saldo en Twilio» de Configuración → Conexión (técnico): la plata que Twilio le cobra a Deenex, que
 * NO es el saldo interno de la marca (ése se descuenta en cada envío y está abajo). Confundirlos
 * haría creer que la marca tiene saldo cuando lo que tiene es Deenex en Twilio, o al revés.
 *
 * Con una subcuenta Twilio no informa saldo (F7): se dice y se manda a la consola de la principal.
 *
 * Con el saldo leído, si alcanza para lo que cargaron las marcas (`AvisoCoberturaSaldo`, el de «Cuenta de Twilio» en
 * Estadísticas): acá llevan la alerta `cobertura_twilio` del Inicio y su banner («Ver saldo del proveedor»), y sin
 * esto la tarjeta mostraba sólo el saldo, sin cuánto falta ni que una recarga en la plataforma no carga Twilio.
 */

import { useCallback, useEffect, useState } from "react";
import { ExternalLink, Landmark } from "lucide-react";
import type { EstadisticasTwilio } from "../../types.estadisticas";
import { estadisticasApi } from "../../estadisticasApi";
import { leerError, leerRespuesta, textoError } from "../../logic/respuesta";
import { ACLARACION_SALDO_TWILIO, ACLARACION_SUBCUENTA, CONSOLA_TWILIO, haceTexto } from "../../logic/estadisticas";
import { usd } from "../../logic/formato";
import { Tarjeta } from "../Tarjeta";
import { AvisoCoberturaSaldo } from "./AvisoCoberturaSaldo";
import { AvisoConectar, ChipSimulado, ErrorTarjeta, Esqueleto } from "./comun";

export function TarjetaSaldoTwilio() {
  const [datos, setDatos] = useState<EstadisticasTwilio | null>(null);
  const [error, setError] = useState("");
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const l = leerRespuesta<EstadisticasTwilio>(await estadisticasApi.twilio());
      if (!l.ok) { setError(textoError(l)); return; }
      if (!l.data) { setError("El servidor no devolvió los datos de Twilio."); return; }
      setDatos(l.data);
      setError("");
    } catch (e) {
      setError(textoError(leerError(e)));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { void cargar(); }, [cargar]);

  const s = datos?.saldo;
  return (
    <Tarjeta titulo="Saldo en Twilio" right={datos?.simulado ? <ChipSimulado /> : undefined}>
      {!datos ? (
        cargando ? <Esqueleto alto="h-16" /> : <ErrorTarjeta texto={`No pudimos traer el saldo de Twilio. ${error}`} onReintentar={() => void cargar()} />
      ) : !datos.disponible ? (
        <AvisoConectar motivo={datos.motivoNoDisponible} />
      ) : s?.noAplica ? (
        <div className="space-y-1">
          <p className="text-sm text-slate-700">{ACLARACION_SUBCUENTA}</p>
          <a href={CONSOLA_TWILIO} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm font-semibold text-violet-700 hover:underline">
            Abrir la consola de Twilio <ExternalLink size={13} />
          </a>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 text-emerald-600 bg-emerald-50"><Landmark size={15} /></div>
            <div>
              <p className="text-sm text-slate-800">
                {s?.valor === null || s?.valor === undefined
                  ? "Todavía no se trajo el saldo de tu cuenta de Twilio."
                  : <><strong className="text-lg text-slate-900">{usd(s.valor)}</strong> en tu cuenta de Twilio{s.at ? ` (actualizado ${haceTexto(s.at, new Date())})` : ""}</>}
              </p>
              <p className="text-xs text-slate-500 mt-1">{ACLARACION_SALDO_TWILIO}</p>
            </div>
          </div>
          {/* Sólo si se pudo comparar: sin el saldo leído, el aviso repetiría «Todavía no se trajo…» y mandaría a «Actualizar
              datos», que está en Estadísticas y no acá. */}
          {typeof datos.coberturaSaldo?.alcanza === "boolean" && (
            <AvisoCoberturaSaldo cobertura={datos.coberturaSaldo} saldoMinimoUSD={datos.alertas?.config?.saldoMinimoUSD} />
          )}
        </div>
      )}
    </Tarjeta>
  );
}

export default TarjetaSaldoTwilio;
